/**
 * ENG-6314: host-side token reconciliation for workflow runs.
 *
 * The in-band workflow_run_* MCP tools (ENG-6310) record a run's timing + a
 * coarse, optional token self-report. This monitor fills the ACCURATE token
 * totals afterwards from the agent transcripts — the same source as
 * token_usage — and posts them back so `workflow_runs.total_*` reflects real
 * usage and "token usage per workflow" aggregates correctly.
 *
 * Why run-grain and not phase-grain: a run's `[started_at, finished_at]` window
 * doesn't overlap itself, so windowed attribution is sound. Per-PHASE windows
 * can overlap under parallel()/pipeline() concurrency, so accurate phase
 * attribution needs the workflow journal (a separate follow-up) — this monitor
 * deliberately stays at the run grain.
 *
 * Token spend lives mostly in the agent() fan-out, whose turns are written to
 * `<session>/subagents/agent-*.jsonl`, so we sum the main session transcripts
 * AND those subagent files over each run's window.
 *
 * Same fire-and-forget contract as the token-usage monitor: self-rate-limited,
 * errors swallowed, never blocks the poll cycle. Idempotent end-to-end — the
 * windowed sum is deterministic and the API stamps `tokens_reconciled_at` so a
 * run is reconciled once (re-posting before the stamp overwrites, never accumulates).
 */

import { readdirSync, readFileSync, statSync } from 'node:fs';
import { join } from 'node:path';
import {
  sumTranscriptUsageInWindow,
  type TranscriptUsageTotals,
} from '@augmented/core';
import { sessionTranscriptDir } from './daily-session.js';
import { getProjectDir } from './persistent-session.js';

const MIN_CHECK_INTERVAL_MS = 5 * 60_000;
// Don't touch a run until its transcript has had a moment to flush after the
// in-band workflow_run_end — avoids reconciling a half-written tail.
const SETTLE_MS = 30_000;
// Only read transcripts touched within this window — bounds per-tick I/O.
const TRANSCRIPT_MTIME_WINDOW_MS = 2 * 24 * 60 * 60 * 1000;
// Safety cap on runs reconciled per tick.
const MAX_RUNS_PER_TICK = 20;

const lastCheckedAt = new Map<string, number>();

/** Test helper — reset module state. Not for production use. */
export function _resetWorkflowReconcilerState(): void {
  lastCheckedAt.clear();
}

/** Minimal API surface — lets tests stub without the real transport. */
export interface WorkflowReconcilerApi {
  get(path: string): Promise<unknown>;
  post(path: string, body: unknown): Promise<unknown>;
}

interface UnreconciledRun {
  id: string;
  agent_id: string | null;
  started_at: string;
  finished_at: string | null;
}

function emptyTotals(): TranscriptUsageTotals {
  return { inputTokens: 0, outputTokens: 0, cacheCreationTokens: 0, cacheReadTokens: 0 };
}

/**
 * Enumerate an agent's transcript JSONL files: the main per-session transcripts
 * directly under the transcript dir, plus every `.jsonl` anywhere under each
 * `<session>/subagents/` (recursively) — both the flat Task-tool layout
 * (`subagents/agent-*.jsonl`) and the nested Workflow-tool layout
 * (`subagents/workflows/<runId>/agent-*.jsonl`), where the agent() fan-out lives.
 *
 * Files last modified before `minMtimeMs` are skipped — a file written before a
 * run started can't contain any of that run's window — which bounds I/O without
 * stranding old work. The caller derives `minMtimeMs` from the earliest run it's
 * reconciling (NOT a fixed "last N days" window, which would permanently exclude
 * a run that wasn't reconciled in time and leave tokens_reconciled_at NULL
 * forever — ENG-6314 review). Defaults to a 2-day lookback when no floor is
 * given. Pure-ish: all I/O is here so the summing logic stays unit-testable.
 */
// Bounded recursion — the subagents tree is shallow (subagents/ →
// [workflows/<runId>/] → agent-*.jsonl), so a small depth cap is ample and
// guards against a pathological/cyclic dir without complicating the walk.
const MAX_SUBAGENT_DEPTH = 6;

/** Recursively collect `.jsonl` files (newer than minMtimeMs) under `dir`. */
function collectJsonlRecursive(
  dir: string,
  minMtimeMs: number,
  out: string[],
  depth: number,
): void {
  if (depth > MAX_SUBAGENT_DEPTH) return;
  let entries: string[];
  try {
    entries = readdirSync(dir);
  } catch {
    return;
  }
  for (const name of entries) {
    const p = join(dir, name);
    let st;
    try {
      st = statSync(p);
    } catch {
      continue; // raced deletion / permission — skip
    }
    if (st.isDirectory()) {
      collectJsonlRecursive(p, minMtimeMs, out, depth + 1);
    } else if (st.isFile() && name.endsWith('.jsonl') && st.mtimeMs >= minMtimeMs) {
      out.push(p);
    }
  }
}

export function enumerateTranscriptFiles(
  transcriptDir: string,
  nowMs: number,
  minMtimeMs: number = nowMs - TRANSCRIPT_MTIME_WINDOW_MS,
): string[] {
  const out: string[] = [];
  let entries: string[];
  try {
    entries = readdirSync(transcriptDir);
  } catch {
    return out;
  }
  for (const name of entries) {
    const path = join(transcriptDir, name);
    let st;
    try {
      st = statSync(path);
    } catch {
      continue;
    }
    if (st.isFile() && name.endsWith('.jsonl')) {
      if (st.mtimeMs >= minMtimeMs) out.push(path);
      continue;
    }
    if (st.isDirectory()) {
      // Recurse the whole `<session>/subagents/` tree. Task-tool subagents sit
      // flat at `subagents/agent-*.jsonl`, but Workflow-tool subagents are
      // nested at `subagents/workflows/<runId>/agent-*.jsonl` (ENG-6322) — a
      // one-level read missed those, undercounting every workflow run.
      collectJsonlRecursive(join(path, 'subagents'), minMtimeMs, out, 0);
    }
  }
  return out;
}

/**
 * Sum windowed token usage across a set of already-read transcript contents.
 * Exported for unit testing the attribution independent of the filesystem.
 */
/**
 * Sum windowed token usage across transcript contents, grouped per model
 * (ENG-6323). The per-model breakdown is what lets a run be priced against
 * model_token_costs — workflow_runs.total_* alone is model-blind.
 */
export function sumWindowedUsageByModelAcrossContents(
  contents: string[],
  startMs: number,
  endMs: number,
): Map<string, TranscriptUsageTotals> {
  const byModel = new Map<string, TranscriptUsageTotals>();
  for (const content of contents) {
    const r = sumTranscriptUsageInWindow(content, startMs, endMs);
    for (const [model, t] of r.byModel) {
      const acc = byModel.get(model) ?? emptyTotals();
      acc.inputTokens += t.inputTokens;
      acc.outputTokens += t.outputTokens;
      acc.cacheCreationTokens += t.cacheCreationTokens;
      acc.cacheReadTokens += t.cacheReadTokens;
      byModel.set(model, acc);
    }
  }
  return byModel;
}

export function sumWindowedUsageAcrossContents(
  contents: string[],
  startMs: number,
  endMs: number,
): TranscriptUsageTotals {
  // Single source of truth: collapse the per-model map so total_* and by_model
  // can never disagree.
  const totals = emptyTotals();
  for (const t of sumWindowedUsageByModelAcrossContents(contents, startMs, endMs).values()) {
    totals.inputTokens += t.inputTokens;
    totals.outputTokens += t.outputTokens;
    totals.cacheCreationTokens += t.cacheCreationTokens;
    totals.cacheReadTokens += t.cacheReadTokens;
  }
  return totals;
}

/**
 * Poll the unreconciled-run work-list for this agent, sum each run's transcript
 * usage over its window, and post the accurate totals back.
 */
export async function maybeReconcileWorkflowRunTokens(args: {
  api: WorkflowReconcilerApi;
  codeName: string;
  agentId: string;
  log: (msg: string) => void;
  now?: Date;
  /** Test seam: override the transcript directory. */
  transcriptDir?: string;
}): Promise<void> {
  const { api, codeName, agentId, log } = args;
  const now = args.now ?? new Date();
  const nowMs = now.getTime();

  const prev = lastCheckedAt.get(codeName);
  if (prev !== undefined && nowMs - prev < MIN_CHECK_INTERVAL_MS) return;
  lastCheckedAt.set(codeName, nowMs);

  let runs: UnreconciledRun[];
  try {
    const res = (await api.get('/host/workflow-runs/unreconciled')) as { runs?: UnreconciledRun[] };
    runs = (res?.runs ?? []).filter(
      (r) =>
        r.agent_id === agentId &&
        !!r.finished_at &&
        nowMs - new Date(r.finished_at).getTime() >= SETTLE_MS,
    );
  } catch (err) {
    log(`[workflow-reconcile] list failed for '${codeName}': ${(err as Error).message}`);
    return;
  }
  if (runs.length === 0) return;

  // Read the agent's transcripts once; reuse across every run this tick. Floor
  // the mtime filter at the earliest run we're reconciling — never a fixed
  // lookback, which would strand a run that aged past the window before its
  // manager got to it. Transcripts last written before any pending run started
  // can't contain those runs' windows, so they're safe to skip.
  const dir = args.transcriptDir ?? sessionTranscriptDir(getProjectDir(codeName));
  const runStartMs = runs
    .map((r) => new Date(r.started_at).getTime())
    .filter((t) => Number.isFinite(t));
  const minMtimeMs =
    runStartMs.length > 0 ? Math.min(...runStartMs) : nowMs - TRANSCRIPT_MTIME_WINDOW_MS;
  const files = enumerateTranscriptFiles(dir, nowMs, minMtimeMs);
  const contents: string[] = [];
  for (const path of files) {
    try {
      contents.push(readFileSync(path, 'utf-8'));
    } catch {
      /* raced deletion / permission — skip */
    }
  }
  if (contents.length === 0) return;

  let reconciled = 0;
  for (const run of runs.slice(0, MAX_RUNS_PER_TICK)) {
    const startMs = new Date(run.started_at).getTime();
    const endMs = new Date(run.finished_at as string).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) continue;

    const byModel = sumWindowedUsageByModelAcrossContents(contents, startMs, endMs);
    // total_* is the rollup of the per-model breakdown — derived from the same
    // map so they can't disagree.
    const totals = emptyTotals();
    const byModelPayload: {
      model: string;
      input_tokens: number;
      output_tokens: number;
      cache_creation_tokens: number;
      cache_read_tokens: number;
    }[] = [];
    for (const [model, t] of byModel) {
      totals.inputTokens += t.inputTokens;
      totals.outputTokens += t.outputTokens;
      totals.cacheCreationTokens += t.cacheCreationTokens;
      totals.cacheReadTokens += t.cacheReadTokens;
      byModelPayload.push({
        model,
        input_tokens: t.inputTokens,
        output_tokens: t.outputTokens,
        cache_creation_tokens: t.cacheCreationTokens,
        cache_read_tokens: t.cacheReadTokens,
      });
    }
    try {
      await api.post(`/host/workflow-runs/${run.id}/reconcile-tokens`, {
        total_input_tokens: totals.inputTokens,
        total_output_tokens: totals.outputTokens,
        total_cache_creation_tokens: totals.cacheCreationTokens,
        total_cache_read_tokens: totals.cacheReadTokens,
        by_model: byModelPayload,
      });
      reconciled++;
    } catch (err) {
      // Leave tokens_reconciled_at NULL so it retries next tick.
      log(`[workflow-reconcile] post failed for run ${run.id}: ${(err as Error).message}`);
    }
  }
  if (reconciled > 0) {
    log(`[workflow-reconcile] reconciled ${reconciled} run(s) for '${codeName}'`);
  }
}
